import React, { createContext, useContext, useState, ReactNode, useEffect, useMemo } from 'react';

interface ChartState {
    title?: string;
    description?: string;
    summary?: ReactNode;
    isEmpty?: boolean;
    headerActions?: ReactNode;
    emptyTitle?: string;
    emptyDescription?: string;
}

interface ChartDispatch {
    setTitle: (title: string) => void;
    setDescription: (description: string) => void;
    setSummary: (summary: ReactNode) => void;
    setIsEmpty: (isEmpty: boolean) => void;
    setHeaderActions: (actions: ReactNode) => void;
    setEmptyTitle: (title: string) => void;
    setEmptyDescription: (description: string) => void;
}

const ChartStateContext = createContext<ChartState | undefined>(undefined);
const ChartDispatchContext = createContext<ChartDispatch | undefined>(undefined);

export const ChartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [title, setTitle] = useState<string | undefined>(undefined);
    const [description, setDescription] = useState<string | undefined>(undefined);
    const [summary, setSummary] = useState<ReactNode | undefined>(undefined);
    const [isEmpty, setIsEmpty] = useState<boolean>(false);
    const [headerActions, setHeaderActions] = useState<ReactNode | undefined>(undefined);
    const [emptyTitle, setEmptyTitle] = useState<string | undefined>(undefined);
    const [emptyDescription, setEmptyDescription] = useState<string | undefined>(undefined);

    const state = useMemo(() => ({
        title,
        description,
        summary,
        isEmpty,
        headerActions,
        emptyTitle,
        emptyDescription
    }), [title, description, summary, isEmpty, headerActions, emptyTitle, emptyDescription]);

    const dispatch = useMemo(() => ({
        setTitle,
        setDescription,
        setSummary,
        setIsEmpty,
        setHeaderActions,
        setEmptyTitle,
        setEmptyDescription
    }), []);

    return (
        <ChartStateContext.Provider value={state}>
            <ChartDispatchContext.Provider value={dispatch}>
                {children}
            </ChartDispatchContext.Provider>
        </ChartStateContext.Provider>
    );
};

export const useChartState = () => {
    const context = useContext(ChartStateContext);
    if (context === undefined) {
        return { isEmpty: false };
    }
    return context;
};

export const useChartDispatch = () => {
    const context = useContext(ChartDispatchContext);
    if (context === undefined) {
        return {
            setTitle: () => { },
            setDescription: () => { },
            setSummary: () => { },
            setIsEmpty: () => { },
            setHeaderActions: () => { },
            setEmptyTitle: () => { },
            setEmptyDescription: () => { }
        };
    }
    return context;
};

export const useChart = () => {
    const state = useChartState();
    const dispatch = useChartDispatch();
    return { ...state, ...dispatch };
};

/**
 * Hook to set chart metadata in one go.
 * Uses only the dispatch context to avoid re-renders when state changes,
 * breaking infinite loops caused by unstable ReactNodes (JSX).
 */
export const useSetChartMetadata = (metadata: {
    title?: string;
    description?: string;
    summary?: ReactNode;
    isEmpty?: boolean;
    actions?: ReactNode;
    emptyTitle?: string;
    emptyDescription?: string;
}) => {
    const {
        setTitle,
        setDescription,
        setSummary,
        setIsEmpty,
        setHeaderActions,
        setEmptyTitle,
        setEmptyDescription
    } = useChartDispatch();

    useEffect(() => {
        if (metadata.title !== undefined) setTitle(metadata.title);
    }, [metadata.title, setTitle]);

    useEffect(() => {
        if (metadata.description !== undefined) setDescription(metadata.description);
    }, [metadata.description, setDescription]);

    useEffect(() => {
        if (metadata.summary !== undefined) setSummary(metadata.summary);
    }, [metadata.summary, setSummary]);

    useEffect(() => {
        if (metadata.isEmpty !== undefined) setIsEmpty(metadata.isEmpty);
    }, [metadata.isEmpty, setIsEmpty]);

    useEffect(() => {
        if (metadata.actions !== undefined) setHeaderActions(metadata.actions);
    }, [metadata.actions, setHeaderActions]);

    useEffect(() => {
        if (metadata.emptyTitle !== undefined) setEmptyTitle(metadata.emptyTitle);
    }, [metadata.emptyTitle, setEmptyTitle]);

    useEffect(() => {
        if (metadata.emptyDescription !== undefined) setEmptyDescription(metadata.emptyDescription);
    }, [metadata.emptyDescription, setEmptyDescription]);
};


